import { useMemo, useState } from 'react';
import type { Classification } from '@heivol-finance/calc';
import { netWorthApi } from '../lib/data.js';
import { parseCsv } from '../lib/csvImport.js';
import { formatOre } from '../lib/format.js';
import { Card, Button } from './ui.js';
import { SectionBar, Tr, Th, Td, TdNum } from './table.js';

/**
 * CSV import into the Net-Worth ledger (Settings → "Importér" card).
 * Paste/upload a CSV of net-worth one-off events, preview the parsed rows,
 * then bulk-create them via the existing netWorthApi.addEvent endpoint.
 * Parsing lives in lib/csvImport.ts.
 */

const CLASS_LABEL: Record<Classification, string> = {
  consumptive: 'Consumptive',
  'asset-converting': 'Asset',
  'debt-service': 'Debt',
};

export function CsvImportCard(): JSX.Element {
  const [text, setText] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const parsed = useMemo(() => (text.trim() ? parseCsv(text) : []), [text]);
  const valid = parsed.filter((r) => !r.error);
  const skipped = parsed.filter((r) => r.error);

  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setResult(null);
      setText(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsText(file);
  };

  const runImport = async () => {
    setImporting(true);
    setResult(null);
    let ok = 0;
    for (const r of valid) {
      try {
        await netWorthApi.addEvent({
          dated: r.dated,
          label: r.label,
          amount_ore: r.amount_ore,
          classification: r.classification,
        });
        ok++;
      } catch {
        /* skip failed row, keep going */
      }
    }
    setImporting(false);
    setResult(`Imported ${ok} of ${valid.length} entries into the net-worth ledger.`);
    if (ok > 0) setText('');
  };

  return (
    <Card className="overflow-hidden">
      <SectionBar title="Import entries (CSV → Net Worth)" />
      <div className="p-4">
        <p className="mb-3 text-hv-sm text-hv-text-secondary">
          Paste or upload a CSV with the columns{' '}
          <span className="font-mono text-hv-xs">date,label,amount,type</span>. Date is YYYY-MM-DD, amount is a number
          (da-DK or plain), and type is <span className="font-mono text-hv-xs">consumptive</span>,{' '}
          <span className="font-mono text-hv-xs">asset</span> or <span className="font-mono text-hv-xs">debt</span>{' '}
          (default consumptive). A header row is tolerated.
        </p>

        <textarea
          value={text}
          onChange={(e) => {
            setResult(null);
            setText(e.target.value);
          }}
          rows={6}
          placeholder={'date,label,amount,type\n2026-05-01,New roof,100000,asset\n2026-06-01,Vacation,15000,consumptive'}
          className="w-full rounded-hv-md border border-hv-border bg-hv-surface px-2.5 py-2 font-mono text-hv-xs text-hv-text outline-none focus-visible:shadow-hv-focus"
        />

        <div className="mt-3 flex items-center gap-3">
          <label className="cursor-pointer rounded-hv-md border border-hv-border bg-hv-surface px-3 py-1.5 text-hv-sm text-hv-text hover:bg-hv-surface-alt">
            Choose file…
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
                e.target.value = '';
              }}
            />
          </label>
          {parsed.length > 0 && (
            <span className="text-hv-xs text-hv-text-tertiary">
              {valid.length} valid · {skipped.length} skipped
            </span>
          )}
        </div>

        {parsed.length > 0 && (
          <div className="mt-4 overflow-x-auto rounded-hv-md border border-hv-border">
            <table className="w-full text-hv-sm">
              <thead>
                <Tr head>
                  <Th>Date</Th>
                  <Th>Label</Th>
                  <Th right>Amount</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                </Tr>
              </thead>
              <tbody>
                {parsed.map((r) => (
                  <Tr key={r.line}>
                    <Td>{r.dated || '—'}</Td>
                    <Td>{r.label}</Td>
                    <TdNum>{formatOre(r.amount_ore)}</TdNum>
                    <Td>{CLASS_LABEL[r.classification]}</Td>
                    <Td>
                      {r.error ? (
                        <span className="text-hv-warning-text">{r.error}</span>
                      ) : (
                        <span className="text-hv-success-text">OK</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => void runImport()} disabled={importing || valid.length === 0}>
            {importing ? 'Importing…' : `Import ${valid.length} entries`}
          </Button>
          {result && <span className="text-hv-sm text-hv-success-text">{result}</span>}
        </div>
      </div>
    </Card>
  );
}
